'use client';

import { useTranslations, useMessages } from 'next-intl';
import {
  translateWithSelfFallback,
  type TranslationValues,
} from './translateWithSelfFallback';

function getNested(obj: unknown, path: string): string | undefined {
  if (obj == null || typeof obj !== 'object') return undefined;
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

/**
 * Same as useTranslations(namespace) but the returned t() will resolve keys
 * that are parent nodes with _self (from unflattened flat dot-notation messages)
 * by trying key._self first; if that is a string it is returned, otherwise t(key, values)
 * is used. Use this in app-router client components that need those labels.
 * Supports an optional second argument for interpolation (e.g. t('key', { time: '9am' })).
 */
export function useTranslationWithSelfFallback(namespace: string) {
  const t = useTranslations(namespace);
  const messages = useMessages() as Record<string, unknown>;
  const ns = messages?.[namespace];

  return function translate(key: string, values?: TranslationValues): string {
    // next-intl throws INVALID_TAG for raw HTML in message values (e.g. <a href="...">).
    // Components that render via dangerouslySetInnerHTML just need the raw string.
    // Precheck before calling t() so the error is never thrown in the first place.
    const raw = getNested(ns, key);
    if (typeof raw === 'string' && raw.includes('<')) {
      return raw;
    }
    return translateWithSelfFallback(
      (translationKey, translationValues) =>
        t(translationKey, translationValues as Parameters<typeof t>[1]),
      key,
      values,
      (selfKey) => typeof getNested(ns, selfKey) === 'string'
    );
  };
}
